/**
 * The menu, as data.
 *
 * Thirteen buttons in one row was the whole navigation until now, and on a phone
 * it became a strip that had to be scrolled sideways to reach anything. Grouping
 * them is not decoration: "Печать" and "Пользователи" have nothing to do with
 * each other beyond both being things you do rarely, and a flat list gives the
 * reader no way to know that.
 *
 * Two entries that used to be here are deliberately gone:
 *   - Scanning is now an action rather than a place. It is a button in the
 *     header, the middle of the phone's bottom bar, and — with a wired scanner —
 *     something that works on whatever screen you are already on. The `/scan`
 *     route still exists for arrivals from a printed QR and for binding an
 *     unknown barcode.
 *   - "Остатки" grew into "Склад": the same low-stock report and integrity check,
 *     now next to Purchases where they belong instead of alone under a name that
 *     promised a warehouse and delivered two buttons.
 */

export interface NavItem {
  /** i18n key under `nav.` and the React key. */
  key: string;
  path: string;
  /** Permission required to see it at all; absent means everyone. */
  permission?: string;
}

export interface NavGroup {
  /** i18n key under `nav.group.`. */
  key: string;
  items: NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    key: 'catalogue',
    items: [
      { key: 'components', path: '/components' },
      { key: 'locations', path: '/locations' },
    ],
  },
  {
    key: 'warehouse',
    items: [
      { key: 'stock', path: '/stock' },
      { key: 'stocktakes', path: '/stocktakes', permission: 'stocktake.run' },
      { key: 'purchases', path: '/purchases', permission: 'purchase.write' },
    ],
  },
  {
    key: 'engineering',
    items: [
      { key: 'specifications', path: '/specs' },
      { key: 'projects', path: '/projects' },
    ],
  },
  {
    key: 'system',
    items: [
      // How this machine reads codes. Everyone sees it: it is a property of the
      // computer in front of them, not of what they are allowed to do.
      { key: 'settings', path: '/settings' },
      // The desk with the printer on it: shown only to people who can print,
      // because on a phone in the aisles it is not the right screen and the
      // queue is filled from there, not emptied.
      { key: 'station', path: '/print', permission: 'label.print' },
      // Migrating an old database writes to the catalogue and posts stock, so it
      // sits behind the catalogue permission — and out of the way of everyone
      // who will never run one.
      { key: 'imports', path: '/imports', permission: 'catalog.write' },
      { key: 'users', path: '/users', permission: 'user.manage' },
      { key: 'audit', path: '/audit', permission: 'audit.read' },
      { key: 'backups', path: '/backups', permission: 'backup.manage' },
      // Which Android build the site hands out. Behind the same permission as
      // backups: both are running the installation rather than using it.
      { key: 'release', path: '/release', permission: 'backup.manage' },
    ],
  },
];

/** The four that fit on the bottom bar of a phone, in reaching order. */
export const PHONE_ITEMS: readonly NavItem[] = [
  { key: 'components', path: '/components' },
  { key: 'locations', path: '/locations' },
  { key: 'stock', path: '/stock' },
];
